import React, { useState } from "react";
import { Box, IconButton, Badge, Modal, Fade, Backdrop } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";

const AnnouncementBell = ({ isDarkMode = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(true);

  const notifications = [
    {
      id: 1,
      title: "Tool Login Functionality Under Maintenance",
      message:
        "We're currently performing maintenance on our tool login system. Some features may be temporarily unavailable.",
      type: "maintenance",
      timestamp: "10/11/25",
    },
    {
      id: 2,
      title: "Chegg and Quizlet Service Interruptions",
      message:
        "We're experiencing technical difficulties with Chegg and Quizlet integrations. Our team is working on a fix.",
      type: "outage",
      timestamp: "10/11/25",
    },
  ];

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setHasUnreadAlerts(false); // Mark notifications as read when modal opens
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "maintenance":
        return "M";
      case "outage":
        return "!";
      default:
        return "N";
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "maintenance":
        return "#FFA500";
      case "outage":
        return "#FF4444";
      default:
        return "#007CFF";
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpenModal}
        sx={{
          color: isDarkMode ? "white" : "#000000",
          backgroundColor: "transparent",
          "&:hover": {
            color: "#007CFF",
            backgroundColor: "transparent",
          },
          "&:focus": {
            outline: "none",
          },
        }}
      >
        <Badge
          badgeContent={hasUnreadAlerts ? "!" : 0}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#FF4444",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "bold",
              minWidth: "18px",
              height: "18px",
              borderRadius: "9px",
              display: hasUnreadAlerts ? "flex" : "none",
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(10px)",
          },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Fade in={isModalOpen}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "420px",
              backgroundColor: "#1a1a1a",
              borderRadius: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow:
                "0 25px 50px rgba(0, 0, 0, 0.5), 0 10px 25px rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              overflow: "hidden",
              animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
              "@keyframes slideUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(30px) scale(0.95)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0) scale(1)",
                },
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                padding: "24px 24px 16px 24px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box>
                  <Box
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "white",
                      lineHeight: 1.2,
                    }}
                  >
                    System Notifications
                  </Box>
                  <Box
                    sx={{
                      fontSize: "14px",
                      color: "rgba(255, 255, 255, 0.6)",
                      marginTop: "2px",
                    }}
                  >
                    {notifications.length} active alert
                    {notifications.length !== 1 ? "s" : ""}
                  </Box>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  "&:hover": {
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Notifications List */}
            <Box sx={{ padding: "16px 24px 24px 24px" }}>
              {notifications.map((notification, index) => (
                <Box
                  key={notification.id}
                  sx={{
                    padding: "16px",
                    marginBottom:
                      index < notifications.length - 1 ? "12px" : "0",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    transition: "all 0.2s ease",
                    position: "relative",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                    },
                  }}
                >
                  {/* Green blinking dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#30D158",
                      animation: "blink 1.5s infinite",
                      "@keyframes blink": {
                        "0%, 50%": {
                          opacity: 1,
                        },
                        "51%, 100%": {
                          opacity: 0.3,
                        },
                      },
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "white",
                        lineHeight: 1.3,
                        marginBottom: "4px",
                      }}
                    >
                      {notification.title}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "13px",
                        color: "rgba(255, 255, 255, 0.7)",
                        lineHeight: 1.4,
                        marginBottom: "8px",
                      }}
                    >
                      {notification.message}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.5)",
                        fontWeight: "500",
                      }}
                    >
                      {notification.timestamp}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                padding: "16px 24px",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.5)",
                  textAlign: "center",
                  lineHeight: 1.4,
                }}
              >
                We'll update you when these issues are resolved
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default AnnouncementBell;
